/* ============================================================
   storage — who may do what, per drive (admins only).

   One matrix per drive: a row for "every non-admin" and a row
   per person, a column per ability. Each person's cell either
   inherits the row above it or says allow / deny outright; the
   default row inherits the built-in default (everything, except
   the direct route, which is only for people who can reach it).
   Admins always have everything and are not in the matrix.

   The server enforces all of it on every route (see "Per-drive
   permissions" in server/nas.py); this only sets it.
   ============================================================ */

import React, { useCallback, useEffect, useState } from 'react';
import { api } from './api';
import { Icon } from './lib';

const LABELS = {
  see: ['See it', 'The drive exists for them at all'],
  browse: ['Browse', 'List folders and files, search, open details'],
  download: ['Download', 'Download, preview, play, copy from it'],
  upload: ['Upload', 'Upload, make folders, copy or move in, edit and rename'],
  delete: ['Delete', 'Delete, trash, empty trash, move out'],
  share: ['Share', 'Make public links to what is on it'],
  trim: ['Trim', 'Trim videos on it'],
  direct: ['Direct', 'Transfer straight to the drive’s own box over the local network'],
};

export default function Permissions({ token, toast }) {
  const [data, setData] = useState(null);
  const [drive, setDrive] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await api.adminPerms(token);
      setData(d); setErr('');
      setDrive((cur) => cur || d.drives[0]?.id);
    } catch (e) { setErr(e.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const set = async (userId, perm, value) => {
    setBusy(`${userId}:${perm}`);
    try {
      setData(await api.setPerm(token, drive, userId, perm, value));
    } catch (e) { toast?.('err', 'Could not change that', e.message); }
    finally { setBusy(''); }
  };

  if (err) return <div className="alert alert--err st-alert">{err}</div>;
  if (!data) return <div className="panel"><div className="skeleton st-skel"/></div>;

  const d = data.drives.find((x) => x.id === drive) || data.drives[0];
  const perms = data.perms.filter((p) => p !== 'direct' || d.direct);
  const set_ = data.set[d.id] || {};
  const defaults = set_[''] || {};
  const people = data.users.filter((u) => u.role !== 'admin');
  const admins = data.users.filter((u) => u.role === 'admin');
  // What the default row comes to for one ability.
  const dflt = (p) => (p in defaults ? defaults[p] : data.builtin[p]);

  const Cell = ({ userId, p }) => {
    const own = userId ? (set_[userId] || {})[p] : defaults[p];
    const value = own === undefined ? 'inherit' : own ? 'allow' : 'deny';
    const inherited = userId ? dflt(p) : data.builtin[p];
    const effective = own === undefined ? inherited : own;
    const u = userId && people.find((x) => x.id === userId);
    // `see` switches the whole drive off, so a person without it can do
    // nothing else there, whatever their other cells say.
    const blocked = u && !u.effective[d.id].see && p !== 'see';
    return (
      <td className={`st-perm-cell ${effective && !blocked ? 'is-yes' : 'is-no'} ${value === 'inherit' ? 'is-inherit' : ''}`}>
        <label className="st-perm-pick">
          <Icon name={effective && !blocked ? 'check' : 'close'} size={13}/>
          <select value={value} disabled={busy === `${userId}:${p}`}
                  aria-label={`${LABELS[p][0]} for ${userId ? u?.username : 'every non-admin'} on ${d.label}`}
                  onChange={(e) => set(userId, p, e.target.value)}>
            <option value="inherit">{userId ? `as default (${inherited ? 'yes' : 'no'})` : `built-in (${inherited ? 'yes' : 'no'})`}</option>
            <option value="allow">allow</option>
            <option value="deny">deny</option>
          </select>
        </label>
      </td>
    );
  };

  return (
    <div className="panel st-perms">
      <div className="panel-head st-perms-head">
        <span className="label">Permissions per drive</span>
        <span className="meta">Admins ({admins.map((a) => a.username).join(', ')}) always have everything.</span>
      </div>
      <div className="st-perms-drives">
        {data.drives.map((x) => (
          <button key={x.id} type="button" className={`st-chip ${x.id === d.id ? 'on' : ''}`} aria-pressed={x.id === d.id}
                  onClick={() => setDrive(x.id)}>{x.label}</button>
        ))}
      </div>
      <div className="st-perms-scroll">
        <table className="st-perms-table">
          <thead>
            <tr>
              <th scope="col">Who</th>
              {perms.map((p) => <th key={p} scope="col" title={LABELS[p][1]}>{LABELS[p][0]}</th>)}
            </tr>
          </thead>
          <tbody>
            <tr className="st-perms-default">
              <th scope="row">Every non-admin<span className="meta">the default</span></th>
              {perms.map((p) => <Cell key={p} userId="" p={p}/>)}
            </tr>
            {people.map((u) => (
              <tr key={u.id}>
                <th scope="row">{u.username}</th>
                {perms.map((p) => <Cell key={p} userId={u.id} p={p}/>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="meta st-perms-note">
        A person inherits the default row unless their own cell says otherwise. Enforced by the server on every
        request{d.direct ? '; Direct only helps someone whose device can reach the drive’s box' : ''}.
      </p>
    </div>
  );
}
